<template>
    <div class="kgr-chat-chatwindow">
        <slot name="content">
            <div
                id="chat-message-container"
                class="kgr-scrollable"
                @scroll="scroll($event)"
            >
                <div class="kgr-region">
                    <kgr-loading v-if="loading" />
                    <div v-else>
                        <kgr-empty-data
                            v-if="messages.length === 0"
                            :value="$t('Noch keine Nachrichten')"
                        />
                        <div
                            v-for="message in messages"
                            :key="message.id"
                        >
                            <kgr-chat-message
                                :institution-key="institutionKey"
                                :message="message"
                                :chat="chat"
                                :outgoing="message.authorUuid === author"
                                :translate-to="translateTo"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </slot>
        <slot
            v-if="author"
            name="input"
        >
            <div class="kgr-region kgr-chat-input">
                <form
                    v-if="chat.userIsAllowedToCreateMessages && !chat.isTerminated()"
                    class="kgr-postfix kgr-postfix--gutters kgr-postfix--center"
                    @submit.prevent.stop="send(currentChatMessage)"
                >
                    <div class="kgr-postfix__fluid">
                        <label
                            class="sr-only"
                            for="kgr-chat-message-text-input"
                        >{{ $t('Abschicken') }}:</label>
                        <textarea
                            id="kgr-chat-message-text-input"
                            ref="chatInput"
                            v-model="currentChatMessage.text"
                            class="kgr-input messageArea"
                            autocomplete="off"
                            name="text"
                            :placeholder="$t('Jetzt chatten...')"
                            maxlength="1000"
                            rows="1"
                            @keydown.enter.exact.prevent="send(currentChatMessage)"
                            @input="autoResize"
                        ></textarea>
                    </div>
                    <div class="kgr-postfix__fixed">
                        <div class="kgr-actionButton">
                            <button class="kgr-actionButton__button kgr-actionButton--small kgr-button kgr-button--icon">
                                <span class="sr-only">Send</span>
                                <kgr-icon
                                    icon-class="arrow-right"
                                    class="kgr-actionButton__icon"
                                />
                            </button>
                        </div>
                    </div>
                </form>
                <div
                    v-else-if="chat.isTerminated()"
                    class="kgr-alert kgr-alert--warning"
                >
                    {{ $t('Der Chat ist beendet.') }}
                </div>
                <div
                    v-else
                    class="kgr-alert kgr-alert--warning"
                >
                    {{ $t('Keine Berechtigung, eigene Nachrichten in diesem Chat zu schreiben.') }}
                </div>
            </div>
        </slot>
    </div>
</template>

<script>
import ChatEntry from '../../js/dto/ChatEntry.js';
import ChatMessage from '../../js/dto/ChatMessage.js';
import ChatMessageLine from './ChatMessageLine.vue';
import EmptyData from '../../components/common/EmptyData.vue';
import Error from '../../js/model/Error.js';
import Icon from '../common/icon/Icon.vue';
import Loading from '../common/Loading.vue';
import axios from 'axios';
import { getLanguageFromCookie } from '../../js/service/LanguageHelper.js';

export default {
    components: {
        'kgr-loading': Loading,
        'kgr-chat-message': ChatMessageLine,
        'kgr-empty-data': EmptyData,
        'kgr-icon': Icon,
    },
    props: {
        chat: {
            type: ChatEntry,
            required: true,
        },
        institutionKey: {
            type: String,
            required: true,
        },
        author: {
            type: String,
            required: false,
            default: null,
        },
    },
    data() {
        return {
            messages: [],
            stringMessage: null,
            loading: true,
            scrolled: false,
            container: null,
            currentChatMessage: new ChatMessage(),
            autoloadTimer: null,
            translateTo: getLanguageFromCookie(),
        };
    },
    computed: {
        messagePostRoute() {
            switch (this.institutionKey) {
                case 'club':
                    return `/club/chat/${this.chat.id}/post/`;
                case 'kindergarten':
                    return `/chat/${this.chat.id}/post/`;
                default:
                    throw new Error(`Invalid institution key: ${this.institutionKey}`);
            }
        },
        chatRoute() {
            switch (this.institutionKey) {
                case 'club':
                    return `/club/chat/${this.chat.id}`;
                case 'kindergarten':
                    return `/chat/${this.chat.id}`;
                default:
                    throw new Error(`Invalid institution key: ${this.institutionKey}`);
            }
        },
    },
    watch: {
        chat() {
            this.loading = true;
            this.init();
        },
        messages() {
            if (!this.scrolled) {
                setTimeout(this.scrollToBottom, 25);
            }
        },
    },
    mounted() {
        this.container = this.$el.querySelector('#chat-message-container');
        this.init();
    },
    beforeUnmount() {
        clearInterval(this.autoloadTimer);
        this.autoloadTimer = null;
    },
    methods: {
        init() {
            this.stringMessage = null;
            this.load();
            if (this.autoloadTimer) {
                clearInterval(this.autoloadTimer);
            }
            this.autoloadTimer = setInterval(this.load, 10000);
        },
        load() {
            axios({
                url: this.chatRoute,
                method: 'get',
            })
                .then((response) => {
                    const messages = response.data.data.map((message) => new ChatMessage(message));
                    const s = JSON.stringify(messages);
                    if (this.stringMessage === null) {
                        this.stringMessage = s;
                        this.messages = messages;
                    } else if (this.stringMessage !== s) {
                        this.stringMessage = s;
                        this.messages = messages;
                    }
                })
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => {
                    this.loading = false;
                });
        },
        send(message) {
            const payload = {
                text: message.text.trim(),
            };
            if (payload.text.length === 0) {
                return;
            }
            message.text = '';
            this.$refs.chatInput.style.height = '48px';

            axios({
                url: this.messagePostRoute,
                method: 'post',
                data: payload,
            })
                .then(() => {
                    this.load();
                    this.$emit('newMessage');
                });
        },
        autoResize() {
            const el = this.$refs.chatInput;
            const lineBreaks = (this.currentChatMessage.text.match(/\n/g) || []).length;
            el.style.height = '48px';
            el.style.height = `${Math.max(48, el.scrollHeight + 2 + (lineBreaks > 0 ? 12 : 0))}px`;
        },
        scrollToBottom() {
            this.container.scrollTop = this.container.scrollHeight;
            this.scrolled = false;
        },
        scroll() {
            this.scrolled = this.container.scrollTop !== (this.container.scrollHeight - this.container.clientHeight);
        },
    },
};
</script>

<style scoped lang="scss">
#chat-message-container {
    flex: 1 1 100%;
}

textarea.messageArea {
    resize: none;
    overflow-y: auto;
    min-height: unset;
    height: 48px;
    max-height: 200px;
    padding-top: 14px;
}

.kgr-chat-chatwindow {
    height: 100%;
    display: flex;
    flex-direction: column;
}
</style>
